import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Wallet, RotateCcw, Clock } from "lucide-react";
import { api } from "../../lib/api";
import { Card, PageHeader, EmptyState, Spinner, StatusPill } from "../../components/dashboard/DashboardShell";
import { Button } from "../../components/ui/button";
import { rupiah, fmtDate, PAYMENT_LABELS } from "../../lib/format";

const PAY_STYLE = { pending: "bg-amber-50 text-amber-600", paid_held: "bg-blue-50 text-blue-600", released: "bg-emerald-50 text-emerald-600", refunded: "bg-slate-100 text-slate-600", cancelled: "bg-slate-100 text-slate-500", failed: "bg-red-50 text-red-500" };

export default function PaymentsPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["orders", "all"], queryFn: () => api.get("/orders").then((r) => r.data) });
  const sum = (st) => data?.filter((o) => o.payment_status === st).reduce((s, o) => s + o.price, 0) || 0;
  const unpaid = data?.filter((o) => o.status === "menunggu_bayar") || [];
  return (
    <div>
      <PageHeader title="Pembayaran" subtitle="Riwayat pembayaran dan status dana escrow Anda." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[["Menunggu Bayar", unpaid.reduce((s, o) => s + o.price, 0), Clock, "text-amber-600 bg-amber-50"], ["Ditahan (Escrow)", sum("paid_held"), ShieldCheck, "text-blue-600 bg-blue-50"],
          ["Diteruskan", sum("released"), Wallet, "text-emerald-600 bg-emerald-50"], ["Dikembalikan", sum("refunded"), RotateCcw, "text-slate-600 bg-slate-100"]].map(([l, v, Icon, c]) => (
          <Card key={l}><div className="flex items-center gap-3"><span className={`w-10 h-10 rounded-xl flex items-center justify-center ${c}`}><Icon size={18} /></span><p className="text-xs font-semibold text-slate-400">{l}</p></div><p className="text-xl font-extrabold text-slate-900 mt-3">{rupiah(v)}</p></Card>
        ))}
      </div>
      {unpaid.length > 0 && (
        <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="unpaid-banner">
          <p className="text-sm font-bold text-amber-700">Anda memiliki {unpaid.length} pesanan yang belum dibayar.</p>
          <Button size="sm" onClick={() => navigate(`/pengguna/pesanan/${unpaid[0].order_id}`)} className="bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold rounded-lg">Bayar Sekarang</Button>
        </div>
      )}
      <Card title="Riwayat Transaksi" className="mt-4">
        {isLoading ? <Spinner /> : !data?.length ? <EmptyState icon="creditcard" title="Belum ada transaksi" /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[600px]" data-testid="payments-table">
              <thead><tr className="text-left text-[11px] text-slate-400 font-bold border-b border-slate-100"><th className="pb-3 pr-3">Order</th><th className="pb-3 pr-3">Tanggal</th><th className="pb-3 pr-3">Layanan</th><th className="pb-3 pr-3">Jumlah</th><th className="pb-3 pr-3">Status Dana</th><th className="pb-3">Pesanan</th></tr></thead>
              <tbody>
                {data.map((o) => (
                  <tr key={o.order_id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60 cursor-pointer" onClick={() => navigate(`/pengguna/pesanan/${o.order_id}`)}>
                    <td className="py-3.5 pr-3 font-bold text-slate-700 text-[13px]">{o.order_id}</td>
                    <td className="py-3.5 pr-3 text-slate-500 text-[13px]">{fmtDate(o.created_at)}</td>
                    <td className="py-3.5 pr-3 text-slate-700 font-semibold text-[13px]">Inspeksi {o.vehicle_type_label}</td>
                    <td className="py-3.5 pr-3 font-bold text-slate-800 text-[13px]">{rupiah(o.price)}</td>
                    <td className="py-3.5 pr-3"><span className={`text-[11px] font-bold rounded-full px-2.5 py-1 ${PAY_STYLE[o.payment_status] || "bg-slate-100"}`}>{PAYMENT_LABELS[o.payment_status] || o.payment_status}</span></td>
                    <td className="py-3.5"><StatusPill status={o.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
